/**
 * Upload size limits — the ONE place these are set on the client (AB#6463).
 *
 * Two different numbers used to be conflated, which is what produced the
 * "1 file over 10 MB skipped" report. They are unrelated and are kept apart
 * here deliberately:
 *
 *   1. Per-file-type upload caps — the real limit. Enforced by the SERVER,
 *      which is authoritative: over-cap requests are refused with HTTP 413 /
 *      error code VAL-000003. The client copy exists only so we can say
 *      something accurate BEFORE spending a long upload.
 *
 *   2. SHEET_DISCOVERY_MAX_BYTES — a client-side *parsing* ceiling only. It is
 *      NOT an upload limit and must never block an upload.
 *
 * All values are configured per environment via VITE_* vars (see the table in
 * every .env.<env> file) and fall back to the values below, which mirror the
 * server:
 *
 *   coronis-apps/apps/pkg/utils/uploadlimits/uploadlimits.go  → Default()
 *   wired onto POST /documents/upload via UploadSizeLimitMiddleware
 *   (coronis-apps/apps/pkg/middleware/upload_size_limit.go)
 *
 * Because config.<env>.yaml ships no `upload:` block, Default() is what every
 * deployed environment enforces today — so the fallbacks and the env values
 * should agree. If they ever diverge, the SERVER wins and the client will
 * simply promise the wrong number.
 *
 * Units are MiB (1024-based), matching the server. Var names say MIB for that
 * reason — do not reintroduce ambiguous "MB" names.
 *
 * NOTE (#6463 acceptance): the SPA should ultimately READ these from the API so
 * there is exactly one authoritative cap per record type. The VITE_* indirection
 * is the interim step — it makes the numbers configurable per environment
 * without a rebuild-and-guess, and gives one obvious thing to delete when the
 * endpoint lands.
 */

const MiB = 1024 * 1024;

/**
 * Per-file-type upload caps in MiB, keyed by lowercase extension without the
 * dot. Mirrors uploadlimits.Default().PerFormat.
 *
 * CSV is intentionally LOWER than PDF: the worker extractor reads CSVs fully
 * into memory, whereas PDFs stream to Document Intelligence. Do not raise the
 * CSV tier without a corresponding worker change.
 */
const FALLBACK_LIMITS_MIB: Readonly<Record<string, number>> = {
  pdf: 100,
  xlsx: 50,
  xls: 50,
  csv: 25,
  png: 20,
  jpg: 20,
  jpeg: 20,
  gif: 10,
  txt: 10,
  doc: 50,
  docx: 50,
};

const FALLBACK_DEFAULT_MIB = 100;
const FALLBACK_ABSOLUTE_MIB = 200;
const FALLBACK_SHEET_DISCOVERY_MIB = 10;

/**
 * Parses `VITE_UPLOAD_LIMITS_MIB`, a comma-separated `ext:mib` list, e.g.
 * "pdf:100,xlsx:50,csv:25". Unparseable or non-positive entries are dropped
 * with a warning rather than throwing — a typo in a .env file must not take
 * the upload surface down. An empty or absent value keeps the full fallback.
 */
const parseLimitsMiB = (raw: string | undefined): Readonly<Record<string, number>> => {
  if (!raw?.trim()) return FALLBACK_LIMITS_MIB;

  const parsed: Record<string, number> = {};
  for (const pair of raw.split(",")) {
    if (!pair.trim()) continue;
    const [ext, value] = pair.split(":");
    const mib = Number(value?.trim());
    const key = ext?.trim().toLowerCase().replace(/^\./, "");
    if (!key || !Number.isFinite(mib) || mib <= 0) {
      console.warn(`[uploadLimits] ignoring malformed VITE_UPLOAD_LIMITS_MIB entry: "${pair.trim()}"`);
      continue;
    }
    parsed[key] = mib;
  }

  return Object.keys(parsed).length > 0 ? parsed : FALLBACK_LIMITS_MIB;
};

/** A positive number from an env var, or the fallback. */
const positiveMiB = (raw: string | undefined, fallback: number, name: string): number => {
  if (!raw?.trim()) return fallback;
  const mib = Number(raw.trim());
  if (!Number.isFinite(mib) || mib <= 0) {
    console.warn(`[uploadLimits] ignoring malformed ${name}="${raw}"; using ${fallback} MiB`);
    return fallback;
  }
  return mib;
};

const limitsMiB = parseLimitsMiB(import.meta.env.VITE_UPLOAD_LIMITS_MIB);

/** Per-file-type upload caps in BYTES, keyed by lowercase extension. */
export const UPLOAD_LIMIT_BYTES_BY_EXTENSION: Readonly<Record<string, number>> = Object.fromEntries(
  Object.entries(limitsMiB).map(([ext, mib]) => [ext, mib * MiB])
);

/** Applied to any extension absent from the table above. */
export const DEFAULT_UPLOAD_LIMIT_BYTES =
  positiveMiB(import.meta.env.VITE_UPLOAD_LIMIT_DEFAULT_MIB, FALLBACK_DEFAULT_MIB, "VITE_UPLOAD_LIMIT_DEFAULT_MIB") *
  MiB;

/** Hard server ceiling; no per-format tier may exceed it. */
export const ABSOLUTE_UPLOAD_LIMIT_BYTES =
  positiveMiB(import.meta.env.VITE_UPLOAD_LIMIT_ABSOLUTE_MIB, FALLBACK_ABSOLUTE_MIB, "VITE_UPLOAD_LIMIT_ABSOLUTE_MIB") *
  MiB;

/**
 * Ceiling for parsing a workbook in the browser to enumerate its sheet names.
 *
 * This exists solely to keep XLSX.read off the main thread for pathological
 * inputs — it is NOT an upload policy and carries no relationship to the
 * per-type caps above (a 12 MiB .xlsx is over this ceiling but well under its
 * 50 MiB upload cap, and uploads fine).
 *
 * Over this size the dropzone skips sheet discovery and uploads the file
 * without a sheet picker; the server extracts sheets instead. Raising this in
 * place would freeze the tab — XLSX.read with bookSheets:true still inflates
 * the whole archive — so the real lift is moving discovery into a Web Worker.
 */
export const SHEET_DISCOVERY_MAX_BYTES =
  positiveMiB(
    import.meta.env.VITE_SHEET_DISCOVERY_MAX_MIB,
    FALLBACK_SHEET_DISCOVERY_MIB,
    "VITE_SHEET_DISCOVERY_MAX_MIB"
  ) * MiB;

/** Lowercase extension without the dot, or "" when the name has none. */
export const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot === -1 ? "" : fileName.slice(dot + 1).toLowerCase();
};

/**
 * The upload cap the server will apply to this file, and the extension it was
 * resolved from. Mirrors uploadlimits.Resolve() on the Go side.
 */
export const resolveUploadLimit = (fileName: string): { extension: string; bytes: number } => {
  const extension = extensionOf(fileName);
  return {
    extension,
    bytes: UPLOAD_LIMIT_BYTES_BY_EXTENSION[extension] ?? DEFAULT_UPLOAD_LIMIT_BYTES,
  };
};

/** "50" for exact MiB values, "12.5" otherwise — for use in user-facing copy. */
export const formatMiB = (bytes: number): string => {
  const mib = bytes / MiB;
  return Number.isInteger(mib) ? String(mib) : mib.toFixed(1);
};
